'use client'

import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { LayoutGroup, motion } from 'framer-motion'
import React, { forwardRef, useId } from 'react'
import { TouchTarget } from './button'
import { Link } from './link'

export function Sidebar({ className, ...props }: React.ComponentPropsWithoutRef<'nav'>) {
  return <nav {...props} className={clsx(className, 'flex h-full min-h-0 flex-col')} />
}

export function SidebarHeader({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-b border-zinc-950/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5'
      )}
    />
  )
}

export function SidebarBody({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-1 flex-col overflow-y-auto p-4 [&>[data-slot=section]+[data-slot=section]]:mt-8'
      )}
    />
  )
}

export function SidebarFooter({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-t border-zinc-950/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5'
      )}
    />
  )
}

export function SidebarSection({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  let id = useId()

  return (
    <LayoutGroup id={id}>
      <div {...props} data-slot="section" className={clsx(className, 'flex flex-col gap-0.5')} />
    </LayoutGroup>
  )
}

export function SidebarDivider({ className, ...props }: React.ComponentPropsWithoutRef<'hr'>) {
  return <hr {...props} className={clsx(className, 'my-4 border-t border-zinc-950/5 lg:-mx-4 dark:border-white/5')} />
}

export function SidebarSpacer({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'mt-8 flex-1')} />
}

export function SidebarHeading({ className, children, ...props }: React.ComponentPropsWithoutRef<'h3'>) {
  return (
    <h3 {...props} className={clsx(className, 'mb-1 px-2 text-xs/6 font-medium text-zinc-500 dark:text-zinc-400')}>
      {children}
    </h3>
  )
}

export const SidebarItem = forwardRef(function SidebarItem(
  {
    current,
    className,
    children,
    ...props
  }: { current?: boolean; className?: string; children: React.ReactNode } & (
    | Omit<Headless.ButtonProps, 'as' | 'className'>
    | Omit<Headless.ButtonProps<typeof Link>, 'as' | 'className'>
  ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) {
  let classes = clsx(
    // Base
    'group flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-normal text-zinc-950 sm:py-2 sm:text-sm/5 cursor-pointer',
    // Leading icon/icon-only - lucide-react uses stroke, not fill
    'data-[slot=icon]:*:size-6 data-[slot=icon]:*:shrink-0 sm:data-[slot=icon]:*:size-5',
    '[&>svg]:size-4 [&>svg]:shrink-0',
    // Trailing icon (down chevron or similar)
    'data-[slot=icon]:last:*:ml-auto data-[slot=icon]:last:*:size-5 sm:data-[slot=icon]:last:*:size-4',
    // Avatar
    'data-[slot=avatar]:*:-m-0.5 data-[slot=avatar]:*:size-7 data-[slot=avatar]:*:[--ring-opacity:10%] sm:data-[slot=avatar]:*:size-6',
    // Default icon color - only when not current and not hovered
    // Light mode: gray icons
    !current && '[&>svg]:stroke-zinc-950',
    // Hover - green-500 background with white text and icons
    // Using both hover: and group-hover: with !important to ensure it overrides
    'hover:bg-green-500 hover:text-white hover:[&>span]:text-white',
    'hover:[&>svg]:!stroke-white',
    'hover:[&_svg]:!stroke-white',
    'group-hover:[&>svg]:!stroke-white',
    'group-hover:[&_svg]:!stroke-white',
    'data-[hover]:bg-green-500 data-[hover]:text-white data-[hover]:[&>svg]:!stroke-white data-[hover]:[&_svg]:!stroke-white data-[hover]:[&>span]:text-white',
    // Active
    'data-[active]:bg-zinc-950/5',
    // Current - blue-500 background with white text and icons (higher specificity)
    current && 'bg-blue-500 text-white [&>svg]:!stroke-white [&_svg]:!stroke-white [&>span]:text-white',
    !current && 'data-[current]:text-blue-500 data-[current]:bg-zinc-100',
    // Dark mode - white icons and text by default
    'dark:text-white dark:[&>svg]:stroke-white',
    'dark:hover:bg-green-500 dark:hover:text-white dark:hover:[&>span]:text-white',
    'dark:hover:[&>svg]:!stroke-white',
    'dark:hover:[&_svg]:!stroke-white',
    'dark:group-hover:[&>svg]:!stroke-white',
    'dark:group-hover:[&_svg]:!stroke-white',
    'dark:data-[hover]:bg-green-500 dark:data-[hover]:text-white dark:data-[hover]:[&>svg]:!stroke-white dark:data-[hover]:[&_svg]:!stroke-white dark:data-[hover]:[&>span]:text-white',
    'dark:data-[active]:bg-white/5',
    current && 'dark:bg-blue-500 dark:text-white dark:[&>svg]:!stroke-white dark:[&_svg]:!stroke-white dark:[&>span]:text-white',
    !current && 'dark:data-[current]:text-blue-300 dark:data-[current]:bg-zinc-700',
  )

  return (
    <span className={clsx(className, 'relative')} data-sidebar-item>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className="absolute inset-y-2 -left-4 w-0.5 rounded-full bg-blue-500 dark:bg-blue-300"
          transition={{ type: "spring", stiffness: 500, damping: 35 }}
        />
      )}
      {'href' in props ? (
        <Headless.CloseButton
          as={Link}
          {...props}
          className={classes}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.CloseButton>
      ) : (
        <Headless.Button
          {...(props as Headless.ButtonProps<'button'>)}
          className={clsx('cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  )
})

export function SidebarLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={clsx(className, 'truncate')} />
}
